import { createAsyncThunk } from '@reduxjs/toolkit';
import React from 'react';
import { toast } from 'react-toastify';

import {
  dropzoneEnqueueUploadingFile,
  dropzoneRemoveUploadedFiles,
  dropzoneUpdateUploadingProgress,
} from '~/actions/dropzone';
import { uploadDocuments } from '~/api/document';
import { getUploadLogs } from '~/api/users';
import EcToast from '~/components/Shared/EcToast/EcToast';
import { UploadStatusType } from '~/enums/UploadStatusType';
import { ERROR, SUCCESS } from '~/types/toast.types';

import { slices } from '../slices';

/*************
 * Thunks
 *************/
export const getUserUploads = createAsyncThunk(
  'fileUpload/getUserUploads',
  getUploadLogs,
);

export const openUploadInfoPanelAndGetLogs = createAsyncThunk(
  'fileUpload/openUploadInfoPanelAndGetLogs',
  async (__unused__, thunkAPI) => {
    // open before fetching not after, in case the user closes before the get request resolves
    thunkAPI.dispatch(slices.deprecatedUpload.actions.openUploadInfoPanel());

    await thunkAPI.dispatch(getUserUploads());
  },
);

export const uploadDocumentsThunk = createAsyncThunk(
  'fileUpload/uploadDocuments',
  async ({ files, apiConfig, uploadFormFields }, thunkAPI) => {
    let toastId;
    const hasUploadLogs = ({ service, url }) =>
      service === 'pilot' && url === '/document/upload/';

    const onUploadStart = (file) => {
      const processingFile = {
        id: file.upload.uuid,
        document_name: file.name,
        size: file.size,
        date_added: new Date(),
        progress: 0,
        processing_status: UploadStatusType.Uploading,
      };

      thunkAPI.dispatch(dropzoneEnqueueUploadingFile(processingFile));
    };

    const onUploadProgress = (file, event) => {
      const { total, loaded } = event;
      const progress = (loaded / total) * 100;
      const bytesSent = loaded;
      if (progress === 100 && file.size - bytesSent > 0) return;

      thunkAPI.dispatch(
        dropzoneUpdateUploadingProgress({ id: file.upload.uuid, progress }),
      );
    };

    const onUploadError = (file, error) => {
      const href = hasUploadLogs(apiConfig) ? '/upload' : '#';

      let detail = error?.detail || error;
      let processingStatus = UploadStatusType.UploadFailed;
      if (error?.response?.data?.detail) {
        detail = error.response.data.detail;
        if (detail?.includes('File type not supported')) {
          processingStatus = UploadStatusType.UploadFileTypeFailure;
        }
      }

      if (detail?.includes('DOCTYPE')) {
        detail = 'Service error. Please try again';
      }

      if (!toast.isActive(toastId)) {
        toastId = toast(<EcToast type={ERROR} text={detail} href={href} />);
      } else {
        toast(<EcToast type={ERROR} text={detail} href={href} />);
      }

      thunkAPI.dispatch(
        dropzoneUpdateUploadingProgress({
          id: file.upload.uuid,
          processing_status: processingStatus,
        }),
      );
    };

    const onUploadComplete = (file) => {
      const href = file.uploadCb ? '#' : '/upload';
      toast(
        <EcToast
          type={SUCCESS}
          text={`"${file.name}" has been uploaded successfully.`}
          href={href}
        />,
      );

      thunkAPI.dispatch(dropzoneRemoveUploadedFiles([file.upload.uuid]));
    };

    const options = {
      apiConfig,
      onUploadStart,
      onUploadProgress,
      onUploadError,
      onUploadComplete,
    };

    files.forEach((file) => {
      file['upload_form_data'] = uploadFormFields;
    });

    await uploadDocuments(files, options);

    if (hasUploadLogs(apiConfig)) {
      thunkAPI.dispatch(openUploadInfoPanelAndGetLogs());
    }
  },
);
